import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import { easeIn } from 'components/FormSection/FormSection.styled';

/** Answers of one question, side by side. */
export const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 32px;
  margin-top: 12px;
`;

/** The global events, ticked one under the other. */
export const CheckboxColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
`;

/** A select, a date or a number does not need the width of the whole form. */
export const NarrowInput = styled.div`
  max-width: 28rem;
  width: 100%;
`;

/** Note replacing an input that has nothing to offer. */
export const EmptyNote = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.gray['500']};
`;

/**
 * One reported event, boxed off from the one below it.
 *
 * The card stays white: the inputs of the app are filled with gray 100 and
 * carry no border of their own, so a tinted card would swallow their edges.
 * The border and the accent down the left are what set the card apart, and the
 * inputs inside it are given a border of their own on top of that.
 */
export const EventCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray['200']};
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(29, 28, 34, 0.06);
  padding: 20px;
  margin-bottom: 20px;

  ${breakpoint('md')`
    padding: 24px;
  `}

  input,
  textarea,
  .react-datepicker__input-container input {
    border: 1px solid ${({ theme }) => theme.colors.gray['300']};
  }

  /* react-select draws its own control, so it is bordered through its class
     rather than through the input it hides inside. */
  [class*='-control'] {
    border-color: ${({ theme }) => theme.colors.gray['300']};
  }

  /* Only an event added in this session eases in - opening a saved report
     should not replay the animation for every event that is already there. */
  ${({ appear }) => appear && easeIn}
`;

/** Title of one event card, with the button that drops it on the right. */
export const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray['200']};
`;

export const EventTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.colors.gray['700']};
`;

/** A file input does not need the width of the whole form either. */
export const UploadRow = styled.div`
  max-width: 32rem;
`;

/** The documents of one group, each with the button that drops it. */
export const FileList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  max-width: 44rem;
`;

export const FileRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.colors.gray['200']};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.white};

  & + & {
    margin-top: 8px;
  }

  a {
    text-decoration: underline;
    word-break: break-all;
  }

  ${({ appear }) => appear && easeIn}
`;

/** A file that was picked but not sent yet, so it reads apart from a stored one. */
export const PendingFile = styled.span`
  color: ${({ theme }) => theme.colors.gray['500']};
  font-style: italic;
  word-break: break-all;
`;

/**
 * A button that reads as a link: it sits inside a list of files or next to the
 * title of an event, where a full button would shout over the content.
 */
export const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.bold};
  white-space: nowrap;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
  transition: opacity 150ms ease;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/**
 * Confirmation that a draft was stored, shown next to the button that stored
 * it. The global alert bar sits at the top of a form this long, so on its own
 * it is feedback the user never sees.
 */
export const SavedNote = styled.span`
  ${easeIn}

  font-size: 14px;
  font-weight: ${({ theme }) => theme.font.weight.semibold};
  color: ${({ theme }) => theme.colors.alerts.success.text};
`;

/** Every status a report went through, oldest first. */
export const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-left: 2px solid ${({ theme }) => theme.colors.secondary};

  li {
    position: relative;
    padding: 0 0 16px 16px;
    font-size: 14px;

    &:last-child {
      padding-bottom: 0;
    }
  }
`;

/** Date and author of one status change. */
export const HistoryMeta = styled.span`
  color: ${({ theme }) => theme.colors.gray['500']};
  font-size: 13px;
`;

/** The reason the YCMB gave for a decision. */
export const HistoryReason = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
`;
